import { Autocomplete, AutocompleteRenderInputParams, Box, Popper, SxProps, TextField } from '@mui/material';
import { SyntheticEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Country, getCountries } from 'utils/countries';

import { useLanguage } from '../../hooks/useLanguage';

interface ICountrySelect {
  countryCode: string;
  label: string;
  handleChange: (
    event: SyntheticEvent,
    item: {
      code: string;
      name: string;
      phoneCode: string;
    },
  ) => void;
  classes?: string;
  error?: boolean;
  helperText?: string;
  mode?: 'country' | 'phoneCode';
  required?: boolean;
  disabled?: boolean;
  sx?: SxProps;
}

export const CountrySelect = ({
  countryCode,
  label,
  handleChange,
  classes = '',
  error = false,
  helperText = '',
  mode = 'country',
  required = true,
  disabled,
  ...rest
}: ICountrySelect): JSX.Element => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const getOptionLabel = (c: Country) =>
    mode === 'country' //
      ? c.name
      : c?.phoneCode
        ? `+${c.phoneCode}`
        : '';

  const countries = useMemo(() => getCountries(language), [language]);

  const countryPredicate = useCallback((c: Country) => c.code === countryCode, [countries, countryCode]);

  const countryInputComponent = useCallback(
    (params: AutocompleteRenderInputParams) => {
      const country = countries.find(countryPredicate);

      return (
        <TextField
          className="country-select"
          variant="standard"
          {...params}
          label={label}
          value={country?.name || ''}
          InputLabelProps={{
            shrink: true,
          }}
          error={error}
          helperText={helperText}
          required={required}
        />
      );
    },
    [countryCode, label, error, helperText],
  );

  const phoneCodeInputComponent = useCallback(
    (params: AutocompleteRenderInputParams) => {
      const country = countries.find(countryPredicate);
      const isoCode = country?.code.toLowerCase() ?? 'un';
      const phoneCode = country?.phoneCode ? `+${country.phoneCode}` : '';

      return (
        <TextField
          className="country-select"
          variant="standard"
          {...params}
          label={label}
          value={phoneCode}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <Box sx={{ paddingRight: '8px' }}>
                <img
                  loading="lazy"
                  width="20"
                  src={`https://flagcdn.com/w20/${isoCode}.png`}
                  srcSet={`https://flagcdn.com/w40/${isoCode}.png 2x`}
                  alt=""
                />
              </Box>
            ),
          }}
          InputLabelProps={{
            shrink: true,
          }}
          error={error}
          helperText={helperText}
          required={required}
        />
      );
    },
    [countries, countryCode, label, error, helperText],
  );

  return (
    <Autocomplete
      {...rest}
      disabled={disabled}
      disableClearable={mode === 'phoneCode'}
      noOptionsText={t('components.not-found.target.options')}
      selectOnFocus={true}
      openOnFocus={true}
      className={classes}
      autoSelect={false}
      value={countries.find(countryPredicate) || null}
      onChange={handleChange}
      isOptionEqualToValue={countryPredicate}
      getOptionLabel={getOptionLabel}
      options={countries}
      clearOnEscape
      PopperComponent={(props) => <Popper {...props} sx={{ minWidth: '300px', maxWidth: '500px' }} />}
      filterOptions={(value, { inputValue }) => {
        const input = inputValue.replace('+', '');
        const filtered = value.filter(
          ({ code, name, phoneCode }) => code.includes(input) || name.toLowerCase().includes(input) || phoneCode.includes(input),
        );

        return filtered;
      }}
      renderOption={(props, { code, name, phoneCode }) => (
        <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props} key={`${name}-${code}`}>
          <img
            loading="lazy"
            width="20"
            src={`https://flagcdn.com/w20/${code.toLowerCase()}.png`}
            srcSet={`https://flagcdn.com/w40/${code.toLowerCase()}.png 2x`}
            alt=""
          />
          {mode === 'country' ? `${name} (${code})` : `${name} (+${phoneCode})`}
        </Box>
      )}
      renderInput={mode === 'country' ? countryInputComponent : phoneCodeInputComponent}
      popupIcon={disabled ? null : undefined}
    />
  );
};
